import type {
  BackendMountOptions,
  CoreLoadOptions,
  EmulatorBackend,
  RewindStatus,
  SystemId,
} from '../types';
import type { ShaderId } from '../video/shaders';
import { AudioOutput } from '../audio/AudioOutput';
import { SYSTEMS } from '../systems';
import type { CoreSpec, FromWorker, ToWorker } from '../worker/protocol';

/**
 * エミュレーションを Worker で走らせるバックエンド。
 *
 * 役割分担:
 *   Worker   … コアの実行、OffscreenCanvas への描画、巻き戻し
 *   メイン   … canvas のレイアウト、音声出力、入力収集、セーブの永続化
 *
 * 音声は Worker から PCM を受け取ってメインで鳴らす。AudioContext は
 * Worker から触れないため。入力は変化したときだけ送る（毎フレーム送ると
 * スレッドをまたぐ意味が薄れる）。
 *
 * 実験的な経路。軽いコアではメインスレッド版の方が遅延が小さい。
 */
export class WorkerBackend implements EmulatorBackend {
  readonly id: string;
  readonly systems: readonly SystemId[];
  readonly displayName: string;

  private spec: CoreSpec;
  private worker: Worker | null = null;
  private canvas: HTMLCanvasElement | null = null;
  private container: HTMLElement | null = null;
  private resizeObserver: ResizeObserver | null = null;

  private audio = new AudioOutput();
  private mountOpts: BackendMountOptions | null = null;

  private _running = false;
  private loaded = false;
  private nativeWidth = 0;
  private nativeHeight = 0;
  private displayAspect = 4 / 3;
  private integerScale = false;

  private cheatsAvailable = false;
  private rewindOn = false;
  private rewindActive = false;
  private rewindReason: string | undefined;

  /** 要求 → 応答の対応表 */
  private pending = new Map<number, { resolve: (d?: ArrayBuffer) => void; reject: (e: Error) => void }>();
  private nextRequestId = 1;

  /** load() 中だけ有効な進捗コールバック */
  private loadProgress: CoreLoadOptions['onProgress'];

  /** 直前に送った入力。変化検出に使う。 */
  private lastButtons = new Uint16Array(4);
  private lastAnalog = { leftX: 0, leftY: 0, rightX: 0, rightY: 0 };
  private inputTimer: number | undefined;
  private closed = false;

  constructor(spec: CoreSpec, id: string, systems: readonly SystemId[], displayName: string) {
    this.spec = spec;
    this.id = id;
    this.systems = systems;
    this.displayName = displayName;
  }

  get running(): boolean {
    return this._running;
  }

  /* ------------------------------------------------------------------ */
  /* 起動                                                                */
  /* ------------------------------------------------------------------ */

  async mount(opts: BackendMountOptions): Promise<void> {
    this.mountOpts = opts;
    this.integerScale = opts.integerScale ?? false;

    this.canvas = document.createElement('canvas');
    this.canvas.width = 1;
    this.canvas.height = 1;
    applyCanvasStyle(this.canvas);
    opts.container.appendChild(this.canvas);
    this.container = opts.container;

    this.resizeObserver = new ResizeObserver(() => this.layout());
    this.resizeObserver.observe(opts.container);

    const offscreen = this.canvas.transferControlToOffscreen();

    this.worker = new Worker(new URL('../worker/coreWorker.ts', import.meta.url), {
      type: 'module',
      name: `emu:${this.id}`,
    });
    this.worker.onmessage = (e: MessageEvent<FromWorker>) => this.onMessage(e.data);
    this.worker.onerror = (e) => {
      const message = e.message || 'Worker が異常終了しました';
      this.failWaiters(message);
      opts.onError(new Error(`worker error: ${message}`));
    };
    this.worker.onmessageerror = () => {
      this.failWaiters('Worker メッセージの復号に失敗しました');
      opts.onError(new Error('worker messageerror'));
    };
    const ready = this.waitFor((m) => m.type === 'ready');
    this.send({ type: 'init', spec: this.spec, canvas: offscreen }, [offscreen]);
    await ready;

    try {
      await this.audio.unlock();
    } catch (e) {
      console.warn('[worker-backend] audio unlock deferred', e);
    }
    this.audio.setVolume(opts.volume);

    // 入力は変化したときだけ送る。ゲームパッドはポーリングが要るので
    // 短い間隔で見に行くが、送るのは差分があったときだけ。
    this.inputTimer = window.setInterval(() => this.pumpInput(), 8);
  }

  async load(opts: CoreLoadOptions): Promise<void> {
    if (!this.worker) throw new Error('mount() before load()');

    this.nativeWidth = SYSTEMS[opts.system].nativeWidth;
    this.nativeHeight = SYSTEMS[opts.system].nativeHeight;
    this.displayAspect = SYSTEMS[opts.system].aspect;
    this.loadProgress = opts.onProgress;

    // PS1 のチャンク展開は数分かかることがある。既定 30 秒では切れる。
    const loaded = this.waitFor((m) => m.type === 'loaded', 15 * 60_000);

    // Blob はそのまま渡し（コピーしない）。ArrayBuffer は transfer する。
    const rom = toWorkerBytes(opts.rom);
    const bios = opts.bios ? opts.bios.slice().buffer : undefined;
    const sram = opts.sram ? opts.sram.slice().buffer : undefined;
    const extraFiles = opts.extraFiles
      ? Object.fromEntries(
          Object.entries(opts.extraFiles).map(([name, data]) => [name, toWorkerBytes(data)]),
        )
      : undefined;
    const transfer: Transferable[] = [];
    if (rom instanceof ArrayBuffer) transfer.push(rom);
    if (bios) transfer.push(bios);
    if (sram) transfer.push(sram);
    if (extraFiles) {
      for (const v of Object.values(extraFiles)) {
        if (v instanceof ArrayBuffer) transfer.push(v);
      }
    }

    this.send(
      {
        type: 'load',
        system: opts.system,
        romName: opts.romName,
        rom,
        ...(extraFiles ? { extraFiles } : {}),
        bios,
        ...(opts.biosFilename ? { biosFilename: opts.biosFilename } : {}),
        sram,
      },
      transfer,
    );

    try {
      const msg = (await loaded) as Extract<FromWorker, { type: 'loaded' }>;
      this.nativeWidth = msg.spec.width || this.nativeWidth;
      this.nativeHeight = msg.spec.height || this.nativeHeight;
      this.loaded = true;
      this.layout();
    } finally {
      this.loadProgress = undefined;
    }
  }

  /* ------------------------------------------------------------------ */
  /* メッセージ                                                          */
  /* ------------------------------------------------------------------ */

  private failWaiters(message: string): void {
    for (const { reject } of this.pending.values()) {
      reject(new Error(message));
    }
    this.pending.clear();
    for (const fn of [...this.listeners]) {
      fn({ type: 'error', message });
    }
    this.listeners.clear();
  }

  private send(msg: ToWorker, transfer: Transferable[] = []): void {
    this.worker?.postMessage(msg, transfer);
  }

  private listeners = new Set<(m: FromWorker) => void>();

  private waitFor(match: (m: FromWorker) => boolean, timeoutMs = 30_000): Promise<FromWorker> {
    return new Promise((resolve, reject) => {
      const timeout = window.setTimeout(() => {
        this.listeners.delete(fn);
        reject(new Error('Worker からの応答がありません'));
      }, timeoutMs);

      const fn = (m: FromWorker) => {
        if (m.type === 'error') {
          clearTimeout(timeout);
          this.listeners.delete(fn);
          reject(new Error(m.message));
          return;
        }
        if (!match(m)) return;
        clearTimeout(timeout);
        this.listeners.delete(fn);
        resolve(m);
      };
      this.listeners.add(fn);
    });
  }

  private onMessage(msg: FromWorker): void {
    for (const fn of [...this.listeners]) fn(msg);

    switch (msg.type) {
      case 'audio':
        this.audio.push({ samples: msg.samples, sampleRate: msg.sampleRate });
        return;

      case 'fps':
        this.mountOpts?.onFps?.(msg.value);
        return;

      case 'progress':
        this.loadProgress?.({ percent: msg.percent, label: msg.label });
        return;

      case 'error':
        this.mountOpts?.onError(new Error(msg.message));
        return;

      case 'rewindUnavailable':
        this.rewindReason = msg.reason;
        this.rewindActive = false;
        this.mountOpts?.onRewindUnavailable?.(msg.reason);
        return;

      case 'cheatsSupported':
        this.cheatsAvailable = msg.value;
        return;

      case 'result': {
        const entry = this.pending.get(msg.id);
        if (!entry) return;
        this.pending.delete(msg.id);
        if (msg.ok) entry.resolve(msg.data);
        else entry.reject(new Error(msg.message));
        return;
      }

      default:
        return;
    }
  }

  private request(build: (id: number) => ToWorker, transfer: Transferable[] = []): Promise<ArrayBuffer | undefined> {
    const id = this.nextRequestId++;
    return new Promise((resolve, reject) => {
      this.pending.set(id, { resolve, reject });
      this.send(build(id), transfer);
    });
  }

  /* ------------------------------------------------------------------ */
  /* 入力                                                                */
  /* ------------------------------------------------------------------ */

  private pumpInput(): void {
    const opts = this.mountOpts;
    if (!opts || !this._running) return;

    const snap = opts.pollInput();

    let changed = false;
    for (let i = 0; i < this.lastButtons.length; i++) {
      if (this.lastButtons[i] !== snap.buttons[i]) {
        changed = true;
        break;
      }
    }
    const a = snap.analog;
    if (
      !changed &&
      (Math.abs(a.leftX - this.lastAnalog.leftX) > 0.01 ||
        Math.abs(a.leftY - this.lastAnalog.leftY) > 0.01 ||
        Math.abs(a.rightX - this.lastAnalog.rightX) > 0.01 ||
        Math.abs(a.rightY - this.lastAnalog.rightY) > 0.01)
    ) {
      changed = true;
    }
    if (!changed) return;

    this.lastButtons.set(snap.buttons);
    this.lastAnalog = { ...a };
    this.send({ type: 'input', buttons: new Uint16Array(snap.buttons), analog: { ...a } });
  }

  /* ------------------------------------------------------------------ */
  /* レイアウト（CanvasRenderer と同じ規則）                             */
  /* ------------------------------------------------------------------ */

  private layout(): void {
    const container = this.container;
    const canvas = this.canvas;
    if (!container || !canvas) return;

    const style = getComputedStyle(container);
    const availW = container.clientWidth - parseFloat(style.paddingLeft) - parseFloat(style.paddingRight);
    const availH = container.clientHeight - parseFloat(style.paddingTop) - parseFloat(style.paddingBottom);
    if (availW <= 0 || availH <= 0) return;

    let w = availW;
    let h = w / this.displayAspect;
    if (h > availH) {
      h = availH;
      w = h * this.displayAspect;
    }

    if (this.integerScale && this.nativeHeight > 0) {
      const scale = Math.max(1, Math.floor(h / this.nativeHeight));
      const snapped = this.nativeHeight * scale;
      if (snapped <= availH) {
        h = snapped;
        w = h * this.displayAspect;
      }
    }

    canvas.style.width = `${Math.floor(w)}px`;
    canvas.style.height = `${Math.floor(h)}px`;
  }

  /* ------------------------------------------------------------------ */
  /* 操作                                                                */
  /* ------------------------------------------------------------------ */

  start(): void {
    if (!this.loaded) throw new Error('load() before start()');
    void this.audio.unlock().catch(() => undefined);
    this._running = true;
    this.send({ type: 'start' });
  }

  pause(): void {
    this._running = false;
    this.send({ type: 'stop' });
    void this.audio.suspend();
  }

  resume(): void {
    void this.audio.resume();
    this._running = true;
    this.send({ type: 'start' });
  }

  reset(): void {
    this.send({ type: 'reset' });
    this.audio.flush();
  }

  setVolume(v: number): void {
    this.audio.setVolume(v);
    if (this.mountOpts) this.mountOpts.volume = v;
  }

  setSpeed(mult: number): void {
    // Worker 側の速度変更と同時に、メインの再生リングも空にする。
    this.audio.flush();
    this.send({ type: 'speed', value: mult });
  }

  setCheats(codes: string[]): boolean {
    this.send({ type: 'cheats', codes });
    return this.cheatsAvailable;
  }

  async getDiscInfo(): Promise<import('../types').DiscInfo | null> {
    try {
      const buf = await this.request((id) => ({ type: 'getDiscInfo', id }));
      if (!buf) return null;
      return JSON.parse(new TextDecoder().decode(buf)) as import('../types').DiscInfo;
    } catch {
      return null;
    }
  }

  async swapDisc(index: number): Promise<boolean> {
    try {
      await this.request((id) => ({ type: 'swapDisc', id, index }));
      return true;
    } catch {
      return false;
    }
  }

  /** Worker 側は OffscreenCanvas の 2D 描画なので、シェーダは非対応。 */
  setShader(_id: ShaderId): boolean {
    return false;
  }

  setRewindEnabled(on: boolean): void {
    this.rewindOn = on;
    this.rewindReason = undefined;
    this.send({ type: 'rewindEnabled', value: on });
  }

  setRewinding(on: boolean): void {
    this.rewindActive = on && this.rewindOn;
    this.audio.setMuted(this.rewindActive);
    this.audio.flush();
    this.send({ type: 'rewinding', value: on });
  }

  get rewindStatus(): RewindStatus {
    return {
      available: this.rewindOn && this.rewindReason === undefined,
      active: this.rewindActive,
      // 正確な秒数は Worker 側にしかない。表示用の概算は持たない。
      seconds: 0,
      reason: this.rewindReason,
    };
  }

  async saveState(): Promise<Uint8Array> {
    const data = await this.request((id) => ({ type: 'saveState', id }));
    if (!data) throw new Error('セーブステートを取得できませんでした');
    return new Uint8Array(data);
  }

  async loadState(state: Uint8Array): Promise<void> {
    const buf = state.slice().buffer;
    await this.request((id) => ({ type: 'loadState', id, data: buf }), [buf]);
    this.audio.flush();
  }

  async getSram(): Promise<Uint8Array | null> {
    const data = await this.request((id) => ({ type: 'getSram', id }));
    return data ? new Uint8Array(data) : null;
  }

  async setSram(data: Uint8Array): Promise<boolean> {
    const buf = data.slice().buffer;
    try {
      await this.request((id) => ({ type: 'setSram', id, data: buf }), [buf]);
      return true;
    } catch {
      return false;
    }
  }

  async screenshot(): Promise<Blob | null> {
    try {
      const data = await this.request((id) => ({ type: 'screenshot', id }));
      return data ? new Blob([data], { type: 'image/png' }) : null;
    } catch {
      return null;
    }
  }

  dispose(): void {
    if (this.closed) return;
    this.closed = true;

    if (this.inputTimer) clearInterval(this.inputTimer);
    this.inputTimer = undefined;

    this.resizeObserver?.disconnect();
    this.resizeObserver = null;

    this.audio.dispose();

    // load / mount の waitFor は pending ではなく listeners 側。
    // ここを拒否しないとキャンセル後も start() が最大 15 分宙吊りになり、
    // ROM バッファを掴んだまま再起動できなくなる。
    this.failWaiters('セッションが終了しました');
    this.listeners.clear();

    // Worker が core.load 中だと dispose メッセージは処理されないので、すぐ落とす。
    const worker = this.worker;
    this.worker = null;
    worker?.terminate();

    this.canvas?.remove();
    this.canvas = null;
    this.container = null;
    this._running = false;
    this.loaded = false;
    this.mountOpts = null;
    this.loadProgress = undefined;
  }
}

function toWorkerBytes(data: Uint8Array | Blob): ArrayBuffer | Blob {
  if (data instanceof Blob) return data;
  return data.slice().buffer;
}

function applyCanvasStyle(canvas: HTMLCanvasElement): void {
  const s = canvas.style;
  s.display = 'block';
  s.backgroundColor = '#000';
  s.imageRendering = 'pixelated';
  s.touchAction = 'none';
  s.userSelect = 'none';
  (s as CSSStyleDeclaration & { webkitUserSelect?: string }).webkitUserSelect = 'none';
}
